import Chip from '@material-ui/core/Chip';
import { red } from '@material-ui/core/colors';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import Autocomplete from '@material-ui/lab/Autocomplete';
import React, { useContext, useState } from 'react';
import { Link, LinkProps } from 'react-router-dom';
import AwsRegions from '../../common/AwsRegions';
import MobxContext from '../../mobx/MobxContext';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      maxWidth: 600,
      margin: '64px auto auto auto'
    },
    searchTextField: {
      backgroundColor: theme.palette.background.paper,
      marginBottom: 16
    },
    form: {
      marginBottom: 16
    },
    envAndRegionContainer: {
      marginBottom: 8,
      [theme.breakpoints.up('md')]: {
        display: 'flex',
        marginBottom: 16
      }
    },
    environmentAutocomplete: {
      backgroundColor: theme.palette.background.paper,
      marginBottom: 8,
      minWidth: 200,
      [theme.breakpoints.up('md')]: {
        marginRight: 16,
        marginBottom: 0
      }
    },
    regionAutocomplete: {
      backgroundColor: theme.palette.background.paper,
      minWidth: 200
    },
    recentlyViewedParamSetsSection: {
      marginBottom: 24
    },
    chip: {
      color: theme.palette.text.secondary,
      margin: '0 8px 8px 0'
    },
    chipContainer: {
      display: 'flex',
      flexWrap: 'wrap',
      paddingTop: 8
    },
    title: {
      marginBottom: 24
    },
    quickLinksContainer: {
      marginTop: 40
    },
    errorText: {
      color: red[500]
    }
  })
);

const AdapterLink = React.forwardRef<HTMLAnchorElement, LinkProps>((props, ref) => (
  <Link innerRef={ref as any} {...props} />
));

interface Props {
  onSearch: (environmentName: string, region: string, search: string) => void;
};

function FaroDeploymentLandingPageSearch(props: Props) {
  const { onSearch } = props;
  const classes = useStyles({});
  const [environmentName, setEnvironmentName] = useState(null);
  const [region, setRegion] = useState(null);
  const [search, setSearch] = useState('');
  const [showErrors, setShowErrors] = useState(false);
  const { environmentStore, preferenceStore } = useContext(MobxContext);

  const onSearchInputKeyPress = (event) => {
    // Only handle enter key type
    if (event.which === 13) {
      if (environmentName && region) {
        setShowErrors(false);
        onSearch(environmentName, region, search);
      } else {
        setShowErrors(true);
      }
    }
  }

  const onRegionChange = (event: React.ChangeEvent<{}>, value: any) => {
    if (value) {
      setRegion(value.code);
    }
  };

  const onEnvironmentChange = (event: React.ChangeEvent<{}>, value: any) => {
    if (value) {
      setEnvironmentName(value.EnvironmentName);
    }
  };

  const environment = environmentStore.environments && environmentName ? environmentStore.getEnvironmentByName(environmentName) : null;

  return (
    <div className={classes.root}>
      <Typography variant="h5" className={classes.title}>Search for Deployments</Typography>
      <div className={classes.form}>
        <div className={classes.envAndRegionContainer}>
          <Autocomplete
            size={'medium'}
            fullWidth
            className={classes.environmentAutocomplete}
            options={environmentStore.environments}
            value={environment}
            getOptionLabel={(option) => typeof option === 'string' ? option : option.Alias}
            onChange={onEnvironmentChange}
            getOptionSelected={(option, value) => value ? option.EnvironmentName === value.EnvironmentName : false}
            renderOption={(option, { selected }) => (
              <div>
                <Typography display="block">{option.Alias}</Typography>
                <Typography variant="caption" display="block">{option.AccountID}</Typography>
              </div>
            )}
            renderInput={(params) => <TextField
              {...params}
              placeholder="Environment"
              variant="outlined"
              InputLabelProps={{
                shrink: true
              }} />}
          />
          <Autocomplete
            size={'medium'}
            fullWidth
            className={classes.regionAutocomplete}
            options={AwsRegions || null}
            value={region || null}
            onChange={onRegionChange}
            getOptionLabel={(option) => typeof option === 'string' ? option : option.code}
            getOptionSelected={(option, value) => value ? option.code === value : false}
            renderInput={(params) => <TextField
              {...params}
              placeholder="Region"
              variant="outlined"
              InputLabelProps={{
                shrink: true
              }} />}
          />
        </div>
        <TextField
          id="search"
          size="medium"
          defaultValue={search}
          className={classes.searchTextField}
          fullWidth
          autoComplete="off"
          margin="none"
          variant="outlined"
          placeholder="Search by partial or full deployment name"
          inputProps={{ 'aria-label': 'bare', spellCheck: false }}
          onKeyPress={onSearchInputKeyPress}
          onChange={event => setSearch(event.target.value)}
        />
        {showErrors && (!environmentName || !region) && <Typography variant="caption" className={classes.errorText}>Environment and Region are required</Typography>}
      </div>
      <div>
        <div className={classes.quickLinksContainer}>
          {preferenceStore.recentlyViewedDeployments.length > 0 && <div className={classes.recentlyViewedParamSetsSection}>
            <Typography variant="subtitle2" color="textSecondary">RECENTLY VIEWED DEPLOYMENTS</Typography>
            <div className={classes.chipContainer}>
              {preferenceStore.recentlyViewedDeployments.map(recent => <Chip
                key={`${recent.accountId}:${recent.region}:${recent.name}:${recent.global}`}
                className={classes.chip}
                size="small"
                component={AdapterLink}
                to={`/faro/deployments/${recent.accountId}/${recent.region}/${recent.name}?global=${recent.global}`}
                clickable
                variant="outlined"
                label={recent.name}
              />)}
            </div>
          </div>}
        </div>
      </div>
    </div>
  );
};

export default FaroDeploymentLandingPageSearch;
